import React, { Suspense } from 'react'
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom'

import ErrorBoundary from './components/common/errors/ErrorBoundary'
import DemoModeStartDialog from './components/features/demoMode/DemoModeStartDialog'
import NoEndpointsAlert from './components/features/dialogs/NoEndpointsAlert'
import UnavailabilityAlert from './components/features/dialogs/UnavailabilityAlert'
import PrimaryHeader from './components/features/navigation/PrimaryHeader'
import PrimaryHeaderLargeNotifications from './components/features/navigation/PrimaryHeaderLargeNotifications'
import {
    AboutUsLoadable,
    AccountLoadable,
    ActModuleConsultationPageLoadable,
    ActModuleTransmissionPageLoadable,
    CGULoadable,
    ConformityLoadable,
    ConsultationsPageLoadable,
    DmpLoadable,
    FallBackLoading,
    FeatureListLoadable,
    HelpLoadable,
    HistoryLoadable,
    LegalsLoadable,
    NewsLoadable,
    StripeOnboardingPageLoadable,
    SummaryModelLoadable,
    TransmissionsPageLoadable,
    // UserPaymentsPageLoadable,
    UserPreferencesPageLoadable,
} from './lazyLoading'
import { hideUnavailabilityAlert } from './redux/actions'
import { useAppDispatch, useAppSelector } from './store'
import ThemeWrapper from './theme/ThemeWrapper'
import { clearLocalStorage } from './utils/localStorageUtils'
import { checkIfUserHasAllMandatoryInfosProfile, isMobileOrTabletBrowser } from './utils/utils'
import { OrdoclicSafariGuard } from './components/features/ordoclicSafariGuard/ordoclicSafariGuard'
import Login from './pages/Login'
import GuardsWrapper from './components/common/wrappers/GuardsWrapper'

const PrivateRoute: React.FC<{ logged: boolean }> = ({ logged }) => {
    const location = useLocation()

    if (!logged) {
        return (
            <Navigate
                replace
                to="/"
                state={{
                    from: location.pathname,
                }}
            />
        )
    }
    return <Outlet />
}

const AppEntry: React.FC = () => {
    const dispatch = useAppDispatch()
    const { user, address, extraHealthSystemInfo, extraHealthActivityInfo } = useAppSelector((state) => state.auth)
    const { isSystemUnavailable, unavailabilityMessage } = useAppSelector((state) => state.mainInfos)

    const [, setHasUserAllMandatoryInfos] = React.useState(false)

    React.useEffect(() => {
        clearLocalStorage()
    }, [])

    React.useEffect(() => {
        const checkHasUserAllMandatoryInfos = async (
            userObject: any,
            addressObject: any,
            extraHealthSystemInfoObject: any,
            extraHealthActivityInfoObject: any,
        ) => {
            const result = await checkIfUserHasAllMandatoryInfosProfile(
                userObject,
                addressObject,
                extraHealthSystemInfoObject,
                extraHealthActivityInfoObject,
                true,
            )
            setHasUserAllMandatoryInfos(result)
        }

        checkHasUserAllMandatoryInfos(user, address, extraHealthSystemInfo, extraHealthActivityInfo)
    }, [])

    return (
        <ErrorBoundary>
            <div className={`App-main-container ${isMobileOrTabletBrowser() ? 'App-main-container-mobile' : ''}`}>
                <Router>
                    <ThemeWrapper>
                        <OrdoclicSafariGuard>
                            {user && <PrimaryHeader />}
                            <PrimaryHeaderLargeNotifications />
                            <Suspense fallback={<FallBackLoading />}>
                                <div className="App-main-inner-container">
                                    <Routes>
                                        <Route path="/" element={<Login />} />
                                        <Route path="/" element={<PrivateRoute logged={Boolean(user)} />}>
                                            <Route path="/cgu" element={<CGULoadable />} />
                                            <Route path="/account" element={<AccountLoadable />} />

                                            <Route
                                                element={
                                                    <GuardsWrapper>
                                                        <Outlet />
                                                    </GuardsWrapper>
                                                }
                                            >
                                                <Route path="/about" element={<AboutUsLoadable />} />
                                                <Route path="/legals" element={<LegalsLoadable />} />
                                                <Route path="/dmp" element={<DmpLoadable />} />
                                                <Route path="/feature" element={<FeatureListLoadable />} />
                                                <Route path="/consultations" element={<ConsultationsPageLoadable />} />
                                                <Route path="/transmissions" element={<TransmissionsPageLoadable />} />
                                                {/* disabled for now because it's unused */}
                                                {/* <Route path="/payments" element={<UserPaymentsPageLoadable />} /> */}
                                                <Route
                                                    path="/stripe-onboarding"
                                                    element={<StripeOnboardingPageLoadable />}
                                                />
                                                <Route path="/history" element={<HistoryLoadable />} />
                                                <Route path="/help" element={<HelpLoadable />} />
                                                <Route path="/compatibility" element={<ConformityLoadable />} />
                                                <Route path="/news" element={<NewsLoadable />} />
                                                <Route path="/summary" element={<SummaryModelLoadable />} />
                                                <Route path="/preferences" element={<UserPreferencesPageLoadable />} />
                                                <Route
                                                    path="/consultation"
                                                    element={<ActModuleConsultationPageLoadable />}
                                                />
                                                <Route
                                                    path="/transmission"
                                                    element={<ActModuleTransmissionPageLoadable />}
                                                />
                                            </Route>
                                        </Route>
                                    </Routes>
                                    <DemoModeStartDialog />
                                    {isSystemUnavailable && (
                                        <UnavailabilityAlert
                                            open={isSystemUnavailable}
                                            onClose={() => dispatch(hideUnavailabilityAlert())}
                                            message={unavailabilityMessage}
                                        />
                                    )}
                                    {user && <NoEndpointsAlert />}
                                </div>
                            </Suspense>
                        </OrdoclicSafariGuard>
                    </ThemeWrapper>
                </Router>
            </div>
        </ErrorBoundary>
    )
}

export default AppEntry
